import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { debounce } from 'lodash';
import React, { FC } from 'react';
import { InputWithAddRemove } from 'src/shared/globals';
import { T, validateInputSecurity } from 'src/shared/helpers';
import { validateRequired } from 'src/shared/validation';
import styled from 'styled-components';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import { Icon } from '../../../../../widgets/icon';

interface AttributeOptionListProps {
    isFirstLoad: boolean;
    values: SelectedOptionValue[];
    onAddOption?: (attributeId: string, optionLabel: string) => void;
    onRemoveOption: (attributeId: string, optionId: string, optionLabel: string) => void;
    onEditOption: (newValue: string, attributeId: string, optionId: string) => void;
    isAddVisible?: boolean;
}

interface AttributeContainerItemProps {
    itemID: string;
    children?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLElement>;
    disableDrag?: boolean;
}

// Styled components
const InputListContainer = styled.div`
    > div {
        margin-bottom: 16px;
    }
`;

const AttributeContainer = styled.div`
    display: grid;
    //grid-template-columns: 30px 1fr 1fr 30px;
    grid-template-columns: 1fr 1fr 30px;
    grid-column-gap: 20px;
    align-items: top;
    border-bottom: 1px solid ${(props) => props.theme.app.primary_light};
    padding: 20px 0 10px;
    &:first-child {
        padding: 0 0 10px;
    }
`;

const MovedIcon = styled(Icon)<{ movable?: boolean }>`
    vertical-align: middle;
    ${(props) =>
        props.movable &&
        !props.disabled &&
        `&:hover {
          cursor: grab;
          color: ${props.theme.app.secondary}
      }`}
`;

export const AttributeOptionList: FC<AttributeOptionListProps> = ({ isFirstLoad, values, onAddOption, onRemoveOption, onEditOption, isAddVisible }) => {
    const onChangeOptionValue = debounce(onEditOption, 10);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, optionId: string, optionValueId: string) => {
        onChangeOptionValue(e.currentTarget.value, optionId, optionValueId);
    };
    return (
        <InputListContainer>
            {values.length > 0 &&
                values.map((option, index) => {
                    return (
                        <InputWithAddRemove
                            key={`Attribute-${option.Id}-Option-${index}`}
                            name={`Attribute-${option.Id}-Option-${index}`}
                            showAdd={isAddVisible == undefined ? index == values.length - 1 : isAddVisible}
                            message={`${validateRequired(option.Value.Label) ? T._('Required', 'Admin') : !validateInputSecurity(option.Value.Label).isValid ? T._(`Invalid characters detected: ${validateInputSecurity(option.Value.Label).invalidCharacters.join(', ')}`, 'Admin') : ''}`}
                            placeholder='Add another value'
                            value={option.Value.Label}
                            onChange={(e) => handleInputChange(e, option.Id, option.Value.Id)}
                            onKeyDown={(e) => e.code == 'Enter' && index == values.length - 1 && onAddOption && onAddOption(option.Id, option.Value.Label)}
                            onKeyUp={(e) => {
                                if (e.code == 'Enter') {
                                    let nextInput = document.querySelector(`input[name=Attribute-${option.Id}-Option-${index + 1}]`);
                                    if (nextInput) (nextInput as HTMLElement).focus();
                                }
                            }}
                            validationKey={`AttributeOptionValidation-${option.Id}-${index}`}
                            addCallback={() => onAddOption && onAddOption(option.Id, option.Value.Label)}
                            removeCallback={() => onRemoveOption(option.Id, option.Value.Id, option.Value.Label)}
                            validate={(value) => {
                                return (!isFirstLoad && validateRequired(value, true)) || !validateInputSecurity(value).isValid;
                            }}
                        />
                    );
                })}
        </InputListContainer>
    );
};

export const AttributeContainerItem: FC<AttributeContainerItemProps> = ({ itemID, children, onClick }) => {
    const { attributes, listeners, setNodeRef, transform } = useSortable({ id: itemID });

    const style = {
        transform: CSS.Transform.toString(transform)
    };

    return (
        <AttributeContainer onClick={onClick} style={style} {...attributes}>
            {/* <div ref={setNodeRef} {...listeners}><MovedIcon movable><MoveIcon /></MovedIcon></div> */}
            {children}
        </AttributeContainer>
    );
};
